import { useState, useEffect } from 'react';
import axios from 'axios';
import { MOVES_SORTED } from '../data/competitiveMoves';

const CACHE_KEY = 'moves_list_cache';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 1 semana

/**
 * Returns the full moves list fetched from the API, with localStorage caching.
 * Falls back to the static competitive moves list if the API is unavailable or still loading.
 */
export function useMovesList() {
  const [moves, setMoves] = useState(MOVES_SORTED); // static list as initial value
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try localStorage first
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const { data, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp < CACHE_TTL_MS && Array.isArray(data) && data.length > 0) {
          setMoves(data);
          setLoading(false);
          return;
        }
      }
    } catch { /* cache corrupta, ignorar */ }

    // Fetch from API
    axios.get('/api/moves')
      .then(({ data }) => {
        const apiMoves = data.moves;
        if (!Array.isArray(apiMoves) || apiMoves.length === 0) {
          // Cache still building server-side — keep static list, retry later
          setLoading(false);
          return;
        }
        // Sort alphabetically (already sorted from server, but just in case)
        const sorted = [...apiMoves].sort((a, b) => a.name.localeCompare(b.name));
        setMoves(sorted);
        setLoading(false);
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: sorted, timestamp: Date.now() }));
      })
      .catch(() => {
        // API error — keep static list
        setLoading(false);
      });
  }, []);

  return { moves, loading };
}
